import { useGetSettingsGeneral } from '@agos/api-client/platform'
import { T, tt } from '@agos/shared'
import { PageHeader, Spinner, StatusBadge } from '@agos/ui'
import { AlertTriangle, ArrowRight, Settings2 } from 'lucide-react'
import { Link } from 'wouter'

import {
  INVENTED,
  MIRRORED,
  deviceNote,
  inventedLabel,
  inventedNote,
  mirroredLabel,
  ownerName,
  pageNote,
} from './advanced-settings'

/**
 * `/advanced` — the page that never loaded and never saved.
 *
 * **FB-150.** Seven switches from a `useState` literal, no `fetch` in the file, and an *Uložiť* button with no
 * `onClick`. Three of the seven duplicate real settings that live on `/settings-general` and agree with reality
 * only by coincidence; the other four correspond to no column, no setting and no code. Beneath them, two cards
 * reported *Epson TM-T88V · Pripojená* and *PizzaMajstro-WiFi · Online* with permanently green dots — a printer
 * model, a network name and two statuses, all four hard-coded, with no printer table in the schema and no
 * endpoint that reports either.
 *
 * **Ported as what it is.** The switches are gone, because a control that stores nothing is worse than no
 * control, and the *Uložiť* button with it. What remains says so, shows the three real flags **read-only with
 * their live values** and a link to the page that owns them, and names what actually does the job each invented
 * switch claimed. This is a behaviour change and is recorded in `FRONTEND-BUGS.md` under §0.3.
 *
 * Ported from legacy `pages/advanced-settings.tsx` (115 lines).
 */

export function AdvancedSettingsPage() {
  const general = useGetSettingsGeneral()

  return (
    <div className="space-y-5">
      <PageHeader
        title={tt('Pokročilé nastavenia')}
        description={tt('Čo tu bolo a kde to naozaj je')}
        icon={<Settings2 className="w-5 h-5" />}
      />

      <div
        className="rounded-2xl border border-amber-500/40 bg-amber-500/5 px-4 py-3 flex items-start gap-3"
        data-never-worked
      >
        <AlertTriangle className="w-5 h-5 text-amber-500 shrink-0 mt-0.5" />
        <div className="min-w-0">
          <p className="text-sm font-semibold text-amber-500">
            <T>Táto obrazovka nikdy nič nenastavovala</T>
          </p>
          <p className="text-xs text-muted-foreground mt-0.5">{pageNote()}</p>
        </div>
      </div>

      <section className="bg-card border border-border rounded-2xl overflow-hidden" data-mirrored>
        <div className="px-5 py-4 border-b border-border">
          <h2 className="text-xs font-bold text-muted-foreground uppercase tracking-wider">
            <T>Tri, ktoré existujú - a patria inej obrazovke</T>
          </h2>
        </div>
        <div className="divide-y divide-border">
          {MIRRORED.map((flag) => (
            <div key={flag.key} className="px-5 py-4 flex items-center justify-between gap-4" data-flag={flag.key}>
              <div className="min-w-0 flex-1">
                <p className="font-semibold text-foreground text-sm">{mirroredLabel(flag)}</p>
                <p className="text-xs text-muted-foreground mt-0.5">
                  <T>Nastavuje sa v</T> <em>{ownerName(flag)}</em>.
                </p>
              </div>
              <LiveValue value={general.data?.[flag.key]} loading={general.isLoading} field={flag.key} />
              <Link
                href={flag.ownedByPath}
                data-goto={flag.key}
                className="flex items-center gap-1 text-xs text-primary underline shrink-0"
              >
                <T>Otvoriť</T> <ArrowRight className="w-3 h-3" />
              </Link>
            </div>
          ))}
        </div>
      </section>

      <section className="bg-card border border-border rounded-2xl overflow-hidden" data-invented>
        <div className="px-5 py-4 border-b border-border">
          <h2 className="text-xs font-bold text-muted-foreground uppercase tracking-wider">
            <T>Štyri, ktoré neexistujú nikde</T>
          </h2>
        </div>
        <div className="divide-y divide-border">
          {INVENTED.map((key) => (
            <div key={key} className="px-5 py-4" data-invented-flag={key}>
              <p className="font-semibold text-foreground text-sm">{inventedLabel(key)}</p>
              <p className="text-xs text-muted-foreground mt-0.5">{inventedNote(key)}</p>
            </div>
          ))}
        </div>
      </section>

      <p className="text-xs text-muted-foreground" data-device-note>
        {deviceNote()}
      </p>
    </div>
  )
}

/**
 * The live value of a flag this page does not own.
 *
 * Shown because the three switches were the reason anybody opened this screen, and reading them back is
 * useful. Not editable, because the write belongs to the page that owns it and duplicating the control is how
 * the two got out of step in the first place.
 */
function LiveValue({
  value,
  loading,
  field,
}: {
  value: unknown
  loading: boolean
  field: string
}) {
  if (loading) return <Spinner />
  if (typeof value !== 'boolean') {
    // Unknown is not *off*: an absent flag would otherwise read as a decision somebody made.
    return (
      <span className="text-xs text-muted-foreground shrink-0" data-value={field} data-value-state="unknown">
        <T>nezistené</T>
      </span>
    )
  }
  return (
    <span className="shrink-0" data-value={field} data-value-state={value ? 'on' : 'off'}>
      <StatusBadge color={value ? 'emerald' : 'zinc'} label={value ? tt('zapnuté') : tt('vypnuté')} />
    </span>
  )
}
